import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getComicById, getRelatedComics, generateChapters } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');
  const chapters = comic ? generateChapters(comic) : [];
  const related = comic ? getRelatedComics(comic.id) : [];

  if (!comic) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500 mb-4">漫画不存在或已下架</p>
            <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
              浏览其他漫画
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryInfo = {
    name: comic.category,
    slug: comic.categorySlug,
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />
      <main className="flex-1 pt-20">
        <nav className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4" aria-label="面包屑导航">
          <ol className="flex items-center gap-1.5 text-sm text-foreground-500 flex-wrap">
            <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li><Link to="/category/all" className="hover:text-primary-500 transition-colors cursor-pointer">漫画分类</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li><Link to={`/category/${categoryInfo.slug}`} className="hover:text-primary-500 transition-colors cursor-pointer">{categoryInfo.name}</Link></li>
            <li><i className="ri-arrow-right-s-line text-xs"></i></li>
            <li className="text-foreground-800 font-medium">{comic.title}</li>
          </ol>
        </nav>

        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pb-6">
          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            <div className="lg:w-[280px] shrink-0">
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200 sticky top-24">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - 韩漫在线免费阅读`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">{comic.title}</h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                  comic.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-accent-100 text-accent-700'
                }`}>
                  {comic.status}
                </span>
                <span className="text-sm text-foreground-500">作者：<strong className="text-foreground-700">{comic.author}</strong></span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-eye-line mr-1"></i>
                  {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 次阅读
                </span>
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-yellow-400 text-sm"></i>
                  <span className="text-sm font-medium text-foreground-700">{comic.rating}</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-1.5 mb-5">
                {comic.tags.map(tag => (
                  <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
                <Link
                  to={`/category/${categoryInfo.slug}`}
                  className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors cursor-pointer"
                >
                  {categoryInfo.name}
                </Link>
              </div>

              <div className="bg-background-100 rounded-lg p-4 md:p-5 mb-6">
                <h2 className="text-sm font-semibold text-foreground-700 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex flex-wrap items-center gap-3 mb-6">
                <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer">
                  <i className="ri-play-circle-line mr-1.5"></i>
                  开始阅读 第1话
                </button>
                <button className="px-6 py-2.5 bg-secondary-500 hover:bg-secondary-600 text-background-50 dark:text-foreground-950 font-medium rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer">
                  <i className="ri-bookmark-line mr-1.5"></i>
                  加入收藏
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer">
                  <i className="ri-share-forward-line text-sm"></i>
                </button>
              </div>

              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg font-bold text-foreground-900">章节列表</h2>
                  <span className="text-xs text-foreground-500">共 {chapters.length} 话</span>
                </div>
                <div className="bg-background-100 rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
                  {chapters.map((ch, index) => (
                    <div
                      key={ch.id}
                      className={`flex items-center justify-between px-4 py-3 border-b border-background-200/70 last:border-b-0 hover:bg-background-200/50 transition-colors cursor-pointer ${
                        index === 0 ? 'bg-primary-50/50' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {index === 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-500 text-background-50 dark:text-foreground-950 font-medium whitespace-nowrap">NEW</span>
                        )}
                        <span className="text-sm font-medium text-foreground-700">{ch.title}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-foreground-500">
                        <span>{ch.updatedAt}</span>
                        <span>
                          <i className="ri-eye-line mr-0.5"></i>
                          {ch.views > 10000 ? `${(ch.views / 10000).toFixed(0)}万` : ch.views}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold text-foreground-900">相关推荐</h2>
                <Link to={`/category/${categoryInfo.slug}`} className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                  更多{categoryInfo.name}漫画 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                {related.map(rel => (
                  <Link
                    key={rel.id}
                    to={`/comic/${rel.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={rel.cover}
                        alt={rel.title}
                        title={`${rel.title} - 韩漫在线免费阅读`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          rel.status === '连载中'
                            ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                            : 'bg-accent-500 text-background-50 dark:text-foreground-950'
                        }`}>
                          {rel.status}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                      {rel.title}
                    </h3>
                    <div className="flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-star-fill text-yellow-400 text-[10px]"></i>
                      <span>{rel.rating}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}